import { createContext, useContext, useCallback, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Show, ShowFormData } from "@/types/show";
import * as showService from "@/services/showService";
import { INITIAL_SHOWS } from "@/data/mockShows";
import { useAuth } from "@/contexts/AuthContext";

// Re-export types for backward compatibility
export type { Show, ShowFormData } from "@/types/show";

// ============================================================================
// CONTEXT
// ============================================================================

interface ShowsContextType {
  shows: Show[];
  isLoading: boolean;
  error: Error | null;
  getShow: (id: string) => Show | undefined;
  getShowBySlug: (slug: string) => Show | undefined;
  createShow: (data: ShowFormData) => Promise<Show>;
  updateShow: (id: string, data: ShowFormData) => Promise<void>;
  deleteShow: (id: string) => Promise<void>;
  toggleShowStatus: (id: string) => Promise<void>;
  refetch: () => void;
}

const ShowsContext = createContext<ShowsContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function ShowsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isLoading: authLoading, hasRole } = useAuth();
  const isAdmin = hasRole("admin");

  // Fetch shows from database (only when authenticated)
  const {
    data: dbShows,
    isLoading: isDbLoading,
    error: dbError,
    refetch,
  } = useQuery<Show[]>({
    queryKey: ["shows", user?.id, isAdmin ? "admin" : "user"],
    queryFn: () => showService.getShows(isAdmin),
    enabled: isAuthenticated,
    // Admins rarely need the global show list (admin pages have their own queries).
    // Use aggressive caching to prevent a 528+ row refetch on every route change.
    staleTime: isAdmin ? 5 * 60 * 1000 : 0,
    gcTime: isAdmin ? 10 * 60 * 1000 : 5 * 60 * 1000,
  });

  // While auth is resolving, show loading (empty array) to prevent mock data flash
  // Only show INITIAL_SHOWS for truly unauthenticated contexts (e.g. marketing pages)
  const shows = isAuthenticated ? (dbShows ?? []) : (!authLoading ? INITIAL_SHOWS : []);
  const isLoading = isAuthenticated ? isDbLoading : authLoading;
  const error = isAuthenticated ? dbError : null;

  const getShow = useCallback(
    (id: string): Show | undefined => {
      return shows.find((s) => s.id === id);
    },
    [shows]
  );

  const getShowBySlug = useCallback(
    (slug: string): Show | undefined => {
      return shows.find((s) => s.slug === slug);
    },
    [shows]
  );

  // Create show mutation
  const createMutation = useMutation({
    mutationFn: showService.createShow,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["shows"] });
    },
  });

  const createShow = useCallback(
    async (data: ShowFormData): Promise<Show> => {
      if (!isAuthenticated) {
        throw new Error("Must be logged in to create a show");
      }
      const result = await createMutation.mutateAsync(data);
      return result.show;
    },
    [isAuthenticated, createMutation]
  );

  // Update show mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ShowFormData }) =>
      showService.updateShow(id, data),
    onSuccess: (result) => {
      // Log platform sync results if any failures
      const failures = result.platformResults?.filter((r) => !r.success) || [];
      if (failures.length > 0) {
        console.warn("Some platforms failed to sync:", failures);
      }

      // Immediately patch the cache so the UI reflects changes (e.g. new cover image)
      queryClient.setQueryData<Show[]>(
        ["shows", user?.id, isAdmin ? "admin" : "user"],
        (old) =>
          old?.map((s) =>
            s.id === result.show.id ? { ...s, ...result.show } : s
          )
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["shows"] });
      queryClient.invalidateQueries({ queryKey: ["episodes"] });
    },
  });

  const updateShow = useCallback(
    async (id: string, data: ShowFormData): Promise<void> => {
      if (!isAuthenticated) {
        throw new Error("Must be logged in to update a show");
      }
      await updateMutation.mutateAsync({ id, data });
    },
    [isAuthenticated, updateMutation]
  );

  // Delete show mutation
  const deleteMutation = useMutation({
    mutationFn: showService.deleteShow,
    onSuccess: (result) => {
      if (result?.alreadyQueued) {
        console.info("Show deletion already queued");
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["shows"] });
      queryClient.invalidateQueries({ queryKey: ["episodes"] });
      queryClient.invalidateQueries({ queryKey: ["episode-platforms"] });
    },
  });

  const deleteShow = useCallback(
    async (id: string): Promise<void> => {
      if (!isAuthenticated) {
        throw new Error("Must be logged in to delete a show");
      }
      await deleteMutation.mutateAsync(id);
    },
    [isAuthenticated, deleteMutation]
  );

  // Toggle status mutation
  const toggleMutation = useMutation({
    mutationFn: showService.toggleShowStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shows"] });
    },
  });

  const toggleShowStatus = useCallback(
    async (id: string): Promise<void> => {
      if (!isAuthenticated) {
        throw new Error("Must be logged in to toggle show status");
      }
      await toggleMutation.mutateAsync(id);
    },
    [isAuthenticated, toggleMutation]
  );

  return (
    <ShowsContext.Provider
      value={{
        shows,
        isLoading,
        error: error as Error | null,
        getShow,
        getShowBySlug,
        createShow,
        updateShow,
        deleteShow,
        toggleShowStatus,
        refetch,
      }}
    >
      {children}
    </ShowsContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useShows() {
  const context = useContext(ShowsContext);
  if (context === undefined) {
    throw new Error("useShows must be used within a ShowsProvider");
  }
  return context;
}
